import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

export interface ImagenDelVisor {
  /** Clave estable, para el `key` y para saber cuál se está mirando. */
  id: string;
  /** URL ya resuelta (firmada, en nuestro caso). */
  url: string;
  /** Línea de pie: cuándo se tomó, quién la subió, lo que haga falta. */
  pie?: string;
}

interface Props {
  imagenes: ImagenDelVisor[];
  /** Índice de la que se abre. `null` = cerrado. */
  indice: number | null;
  onClose: () => void;
  onCambiar: (indice: number) => void;
}

/**
 * Visor de imágenes a pantalla completa, dentro de la app.
 *
 * **Por qué existe:** la galería abría cada foto con `window.open`, y en el
 * celular eso te saca de la aplicación — el dueño lo midió en su teléfono
 * (2026-09-22) y pidió verla aquí dentro. Volver atrás desde una pestaña nueva
 * en un móvil no es un clic: es salir y buscar la app otra vez.
 *
 * **Se queda un enlace a «abrir original»**, y no es redundante: es lo que
 * permite descargar la foto, hacer zoom con los gestos del sistema y —el caso
 * que de verdad lo justifica— abrir un PDF, que no se puede incrustar con una
 * URL firmada de GCS sin depender de cabeceras del bucket que no controlamos.
 *
 * ⚠️ **El Escape se escucha en fase de CAPTURA y corta la propagación por
 * completo.** Este visor se monta encima de un `Modal`, que también cierra con
 * Escape sobre `window`: sin esto, una sola pulsación cierra los dos y el
 * usuario pierde el detalle de la novedad por haber mirado una foto. Medido
 * el 2026-09-22 — y hace falta `stopImmediatePropagation`, porque
 * `stopPropagation` no basta cuando los dos listeners cuelgan del mismo
 * target.
 */
export function ImageLightbox({ imagenes, indice, onClose, onCambiar }: Props) {
  const fondoRef = useRef<HTMLDivElement>(null);
  const tocadoEn = useRef<number | null>(null);
  const [cargando, setCargando] = useState(true);

  const abierta = indice !== null ? imagenes[indice] : undefined;
  const hayVarias = imagenes.length > 1;

  const ir = useCallback((paso: number) => {
    if (indice === null || imagenes.length === 0) return;
    // Circular: desde la última, «siguiente» vuelve a la primera. Con tres o
    // cuatro fotos, toparse con un botón muerto molesta más que dar la vuelta.
    onCambiar((indice + paso + imagenes.length) % imagenes.length);
  }, [indice, imagenes.length, onCambiar]);

  useEffect(() => { setCargando(true); }, [abierta?.id]);

  useEffect(() => {
    if (indice === null) return;
    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // `stopImmediatePropagation` y no solo `stopPropagation`, y la
        // diferencia la encontró una prueba: cuando el evento tiene a `window`
        // como target —o cuando otro listener de captura se registró antes—,
        // `stopPropagation` NO detiene a los demás listeners del MISMO target,
        // y el Modal de debajo recibía el mismo Escape. Se cerraban los dos, y
        // el usuario perdía el detalle de la novedad por mirar una foto.
        e.stopImmediatePropagation();
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowRight') {
        ir(1);
      } else if (e.key === 'ArrowLeft') {
        ir(-1);
      }
    };
    window.addEventListener('keydown', alPulsar, true);
    return () => window.removeEventListener('keydown', alPulsar, true);
  }, [indice, onClose, ir]);

  if (indice === null || !abierta) return null;

  // Deslizar para pasar de foto. Es el gesto que se espera en un teléfono, y
  // los botones siguen ahí para quien use ratón o no pueda deslizar.
  const alEmpezarToque = (e: React.TouchEvent) => { tocadoEn.current = e.touches[0].clientX; };
  const alTerminarToque = (e: React.TouchEvent) => {
    if (tocadoEn.current === null || !hayVarias) return;
    const recorrido = e.changedTouches[0].clientX - tocadoEn.current;
    tocadoEn.current = null;
    if (Math.abs(recorrido) > 50) ir(recorrido < 0 ? 1 : -1);
  };

  const ESTILO_FLECHA: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '48px', height: '48px', flexShrink: 0,
    border: 'none', borderRadius: '50%',
    background: 'rgba(255,255,255,0.12)', color: '#FFF', cursor: 'pointer',
  };

  return createPortal(
    <div
      ref={fondoRef}
      onClick={(e) => { if (e.target === fondoRef.current) onClose(); }}
      onTouchStart={alEmpezarToque}
      onTouchEnd={alTerminarToque}
      role="dialog"
      aria-modal
      aria-label="Foto"
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        // Casi negro y SIN desenfoque, al revés que el overlay de los modales:
        // aquí el fondo no tiene que insinuar lo que hay detrás, tiene que
        // desaparecer para que la foto se vea por lo que es.
        background: 'rgba(0,0,0,0.92)',
        display: 'flex', flexDirection: 'column',
        animation: 'fade-in 150ms ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', gap: '0.75rem' }}>
        <span style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.75)', minWidth: 0 }}>
          {hayVarias ? `${indice + 1} de ${imagenes.length}` : 'Foto'}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <a
            href={abierta.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Abrir el original en otra pestaña (para descargarla o ampliarla)"
            style={{ ...ESTILO_FLECHA, width: '44px', height: '44px', textDecoration: 'none' }}
          >
            <ExternalLink size={18} />
          </a>
          <button type="button" onClick={onClose} aria-label="Cerrar la foto" style={{ ...ESTILO_FLECHA, width: '44px', height: '44px' }}>
            <X size={20} />
          </button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0 0.5rem', minHeight: 0 }}>
        {hayVarias && (
          <button type="button" onClick={() => ir(-1)} aria-label="Foto anterior" style={ESTILO_FLECHA}>
            <ChevronLeft size={24} />
          </button>
        )}

        <div style={{ flex: 1, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
          {cargando && (
            <span style={{ position: 'absolute', fontSize: '12.5px', color: 'rgba(255,255,255,0.6)' }}>Cargando…</span>
          )}
          <img
            key={abierta.id}
            src={abierta.url}
            alt={abierta.pie ?? 'Foto de la novedad'}
            onLoad={() => setCargando(false)}
            // `contain` y no `cover`: recortar una foto que alguien subió como
            // prueba de algo es justo lo que no se puede hacer.
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', opacity: cargando ? 0 : 1, transition: 'opacity 150ms ease' }}
          />
        </div>

        {hayVarias && (
          <button type="button" onClick={() => ir(1)} aria-label="Foto siguiente" style={ESTILO_FLECHA}>
            <ChevronRight size={24} />
          </button>
        )}
      </div>

      {abierta.pie && (
        <p style={{ margin: 0, padding: '0.75rem 1rem', textAlign: 'center', fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
          {abierta.pie}
        </p>
      )}
    </div>,
    document.body,
  );
}
